import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Grid,
  CircularProgress,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import { url } from "../../constants/url";
import MDEditor from '@uiw/react-md-editor';
import ImagePreviewModal from "../../components/ui/ImagePreviewModal";

export default function ViewMom() {
  const { projectId, momId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [mom, setMom] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  // Fetch project & mom details
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const config = { headers: { Authorization: token ? `Bearer ${token}` : undefined } };
        
        const [projectRes, momRes] = await Promise.all([
          axios.get(`${url}/api/inhouse-projects/fetch-by/${projectId}`, config),
          axios.get(`${url}/api/mom/${momId}`, config)
        ]);

        if (projectRes.data.project) setProject(projectRes.data.project);
        if (momRes.data.mom) setMom(momRes.data.mom);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };
    if (projectId && momId && token) fetchData();
  }, [projectId, momId, token]);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const config = { headers: { Authorization: token ? `Bearer ${token}` : undefined } };
      await axios.delete(`${url}/api/mom/${momId}`, config);
      navigate(`/progress/edit-report/${projectId}`);
    } catch (err) {
      console.error("Error deleting MOM:", err);
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!mom) {
    return (
      <Container maxWidth="xl" sx={{ p: 3 }}>
        <Typography color="error">MOM not found.</Typography>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>Go Back</Button>
      </Container>
    );
  }

  // Check if legacy fields exist to conditionally render them
  const hasLegacyDetails = mom.meetingTime || mom.location || mom.agenda;
  const hasAttendees = mom.attendees && mom.attendees.length > 0 && mom.attendees.some(a => a.name || a.role);

  return (
    <Box sx={{ p: 1.2, position: 'relative' }}>
      {/* ================= HEADER ================= */}
      <Box sx={{ mb: 3 }}>
        {/* Row 1 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY }}>
              Minutes of Meeting (MOM) #{mom.serialNumber || "-"}
            </Typography>
            <Chip 
              label={mom.status} 
              size="small" 
              color={mom.status === 'APPROVED' ? 'success' : mom.status === 'REJECTED' ? 'error' : 'primary'} 
            />
          </Box>
          <IconButton size="small" onClick={() => navigate(-1)}>
            <Icon name="close3" color={COLORS.ERROR} />
          </IconButton>
        </Box>

        {/* Row 2 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Project Name:
            </Typography>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
              {project?.projectName || "-"}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Date:
            </Typography>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
              {mom.meetingDate ? new Date(mom.meetingDate).toLocaleDateString() : "-"}
            </Typography>
          </Box>
        </Box>

        {/* Row 3 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Address:
            </Typography>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
              {project?.siteAddress || "-"}
            </Typography>
          </Box>
          <Box />
        </Box>

        {/* Row 4 */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Developer:
            </Typography>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
              {project?.developerName || "-"}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Engineer:
            </Typography>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
              {project?.assignedEngineer?.name || "-"}
            </Typography>
          </Box>
        </Box>

        {/* Legacy fields if any */}
        {hasLegacyDetails && (
           <Box mt={1}>
             {mom.meetingTime && (
               <Typography variant="body2" color="textSecondary">Meeting Time: {mom.meetingTime}</Typography>
             )}
             {mom.location && (
               <Typography variant="body2" color="textSecondary">Location: {mom.location}</Typography>
             )}
             {mom.agenda && (
               <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: "pre-wrap" }}>Agenda: {mom.agenda}</Typography>
             )}
           </Box>
        )}

        {/* Divider */}
        <Box sx={{ mt: 2, height: "1px", backgroundColor: COLORS.LIGHT_SECONDARY, opacity: 0.7 }} />
      </Box>

      {hasAttendees && (
        <Box sx={{ mb: 4 }}>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 2 }}>Attendees</Typography>
          <TableContainer>
            <Table size="small" sx={{ border: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "rgba(0,0,0,0.02)" }}>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Role/Designation</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mom.attendees.map((a, i) => (
                  <TableRow key={i}>
                    <TableCell>{a.name}</TableCell>
                    <TableCell>{a.role}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ================= POINTS SECTION ================= */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 2 }}>Points & Actions</Typography>
        <TableContainer>
          <Table size="small" sx={{ border: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "rgba(0,0,0,0.02)" }}>
                <TableCell><strong>Sr No.</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Instruction</strong></TableCell>
                <TableCell><strong>Given By</strong></TableCell>
                <TableCell><strong>Action By</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Close Date</strong></TableCell>
                <TableCell><strong>Remarks</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mom.discussionPoints?.length > 0 ? (
                mom.discussionPoints.map((p, i) => (
                  <TableRow key={i}>
                    <TableCell>{p.pointNumber || i + 1}</TableCell>
                    <TableCell>{p.pointDate ? new Date(p.pointDate).toLocaleDateString() : "-"}</TableCell>
                    <TableCell sx={{ whiteSpace: "pre-wrap" }}>
                      <Box data-color-mode="light" sx={{ color: COLORS.PRIMARY, fontSize: "14px" }}>
                        <MDEditor.Markdown source={p.instruction || p.discussion || p.topic || "-"} style={{ whiteSpace: 'pre-wrap', backgroundColor: 'transparent', color: 'inherit', fontSize: 'inherit' }} />
                      </Box>
                      {p.instructionImages && p.instructionImages.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                          {p.instructionImages.map((img, idx) => (
                            <Box key={idx} onClick={() => setPreviewImage(`${url}${img}`)} sx={{ cursor: 'pointer', width: 50, height: 50, borderRadius: 1, overflow: 'hidden', border: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                              <img src={`${url}${img}`} alt="instruction" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </Box>
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{p.givenBy || p.responsibility || "-"}</TableCell>
                    <TableCell>{p.actionBy || p.actionable || "-"}</TableCell>
                    <TableCell>
                      <Chip 
                        size="small" 
                        label={p.status || "Open"} 
                        color={p.status === "Close" || p.status === "Completed" ? "success" : "warning"} 
                      />
                    </TableCell>
                    <TableCell>{p.closeDate ? new Date(p.closeDate).toLocaleDateString() : "-"}</TableCell>
                    <TableCell>{p.remarks || "-"}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">No points recorded.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {mom.adminRemarks && (
        <Box sx={{ p: 3, border: `1px solid ${COLORS.LIGHT_SECONDARY}`, borderRadius: 2, mb: 3, borderLeft: `4px solid ${COLORS.ERROR}` }}>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}>Admin Remarks</Typography>
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>{mom.adminRemarks}</Typography>
        </Box>
      )}
      
      {/* ================= ACTIONS ================= */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 2, borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
        <Button 
          variant="outlined" 
          color="error" 
          onClick={() => setDeleteDialogOpen(true)} 
          disabled={deleting}
          startIcon={<Icon name="delete" />}
        >
          Delete
        </Button>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ borderColor: COLORS.LIGHT_SECONDARY, color: COLORS.TEXT }}>
          Back
        </Button>
      </Box>

      {/* Image Preview Modal */}
      <ImagePreviewModal
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageUrl={previewImage}
        title="Image Preview"
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
      >
        <DialogTitle sx={{ ...TYPOGRAPHY.H3, color: COLORS.ERROR }}>
          Delete MOM Report
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: COLORS.TEXT }}>
            Are you sure you want to delete this MOM report? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: COLORS.TEXT }}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleting}
            sx={{ textTransform: "none" }}
          >
            {deleting ? <CircularProgress size={24} color="inherit" /> : "Delete Report"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
